import {
  BankProductClosureRequest,
  BankProductRequestWithRateDetail,
  BankProductResponse as BankPortalBankProductResponse,
  BankProductUploadRequestBody,
} from '@b7hio/api-lib/src/bank-portal';
import {
  BankProductRequestWithRateAndAccountDetails,
  BankProductResponse,
  BankProductUpdateWithAccountDetailRequest,
  BondsmithPageListableBankProductPaginatedResponse,
  ListableBankProductResponse,
  ProductProposalResponse,
  RateDetailUpdateRequest,
} from '@b7hio/api-lib/src/ops-portal';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { absurd } from 'fp-ts/function';
import { NextSeo } from 'next-seo';
import { MultiButtonPageHeader } from '../../components';
import {
  BanksProductCreateDialog,
  OpsProductCreateDialog,
  ProductCloseDialog,
  ProductList,
  ProductRateChangeDialog,
  ProductUpdateDialog,
} from './components';
import { FSCSUploadDialogBank } from './components/FSCSUploadDialogBank/FSCSUploadDialogBank';
import { ProductListFilterValues } from './components/ProductListFilter/ProductListFilter.validation';
import { ProductUploadAllDialog } from './components/ProductUploadAllDialog/ProductUploadAllDialog';
import { FscsDocument } from './hooks/useFscsInteractivity';
import { ProductDocuments } from './hooks/useProductInteractivity';
import { ProductSoftCloseDialog } from './components/ProductSoftCloseDialog/ProductSoftCloseDialog';
import { PlatformProductDialog } from './components/PlatformProductDialog/PlatformProductDialog';
import { useTranslation } from 'react-i18next';

interface CommonProps {
  readonly portal: 'banks' | 'ops';

  // List State
  readonly isLoading: boolean;
  readonly data?: BondsmithPageListableBankProductPaginatedResponse;

  // View State
  readonly state:
    | 'upload'
    | 'upload-brochure'
    | 'list'
    | 'create'
    | 'update'
    | 'update-rate'
    | 'modify-access'
    | 'modify-access-new'
    | 'delete'
    | 'soft-close'
    | 'duplicate'
    | 'view';
  readonly target?: BankProductResponse | BankPortalBankProductResponse;
  readonly proposal?: ProductProposalResponse;

  // Dialog Events
  readonly onRequestCreate: () => void;
  readonly onRequestUpdate: (bank: ListableBankProductResponse) => void;
  readonly onRequestUpdateRate: (bank: ListableBankProductResponse) => void;
  readonly onRequestModifyAccess: (bank: ListableBankProductResponse) => void;
  readonly onRequestDelete: (bank: ListableBankProductResponse) => void;
  readonly onRequestSoftClose: (bank: ListableBankProductResponse) => void;
  readonly onRequestDuplicate: (bank: ListableBankProductResponse) => void;
  readonly onClose: (event: object, reason: string) => void;
  readonly onRequestView?: (bank: ListableBankProductResponse) => void;

  // API Events
  readonly onUpdate: (
    bank: BankProductUpdateWithAccountDetailRequest,
    files: ProductDocuments
  ) => Promise<void>;
  readonly onUpdateRate: (bank: RateDetailUpdateRequest) => Promise<void>;
  readonly onFilterChange: (value: ProductListFilterValues) => void;
  readonly onProposeDelete?: (data: BankProductClosureRequest) => void;
  readonly onDelete?: (data: BankProductClosureRequest) => void;
  readonly onSoftClose?: (data: BankProductClosureRequest) => void;
  // server side pagination and sorting
  readonly pageNumber: number;
  readonly setPageNumber: (pageNum: number) => void;
  readonly sortModel?: GridSortModel;
  readonly setSortModel: (newModel: GridSortModel) => void;
  readonly resultsPerPage: number;
}

type OpsProps = {
  readonly portal: 'ops';
  readonly onCreate: (
    bank: BankProductRequestWithRateDetail,
    files: ProductDocuments
  ) => Promise<void>;
} & CommonProps;

type BankProps = {
  readonly portal: 'banks';
  readonly onRequestUpload: () => void;
  readonly onRequestFscs: () => void;
  readonly onUploadAllProducts: (data: BankProductUploadRequestBody) => void;
  readonly onUploadFscs: (file: FscsDocument) => Promise<void>;
  readonly onDownloadAllProducts: () => void;
  readonly onDownloadFscs: () => Promise<void>;
  readonly downloadUrl: string;
  readonly onCreate: (
    bank: BankProductRequestWithRateAndAccountDetails,
    files: ProductDocuments
  ) => Promise<void>;
} & CommonProps;

type Props = OpsProps | BankProps;

export const ProductListPage = ({
  isLoading,
  data,
  state,
  target,
  proposal,
  onRequestCreate,
  onRequestUpdate,
  onRequestUpdateRate,
  onRequestModifyAccess,
  onRequestDelete,
  onRequestSoftClose,
  onRequestDuplicate,
  onUpdate,
  onUpdateRate,
  onFilterChange,
  onProposeDelete,
  onDelete,
  onSoftClose,
  sortModel,
  setSortModel,
  pageNumber,
  setPageNumber,
  resultsPerPage,
  onRequestView,
  ...rest
}: Props) => {
  const { t } = useTranslation(['common', 'products']);
  const pendingDoc = data?.content.filter((item) => item.state === 'PENDING');

  return (
    <>
      <NextSeo title={t('products:page.title')} />
      <MultiButtonPageHeader
        title={t('products:page.title')}
        buttons={
          rest.portal === 'banks'
            ? [
                {
                  buttonText: t('products:btn.addProduct'),
                  onClickButton: onRequestCreate,
                },
                {
                  buttonText: t('products:btn.uploadAllProducts'),
                  onClickButton: rest.onRequestUpload,
                  pendingProducts:
                    pendingDoc && pendingDoc?.length > 0 ? true : false,
                },
                {
                  buttonText: t('products:btn.fscsBrochure'),
                  onClickButton: rest.onRequestFscs,
                },
              ]
            : [
                {
                  buttonText: t('products:btn.addProduct'),
                  onClickButton: onRequestCreate,
                },
              ]
        }
      />
      <ProductList
        data={data}
        isLoading={isLoading}
        onRequestUpdate={onRequestUpdate}
        onRequestUpdateRate={onRequestUpdateRate}
        onRequestDelete={onRequestDelete}
        onRequestSoftClose={onRequestSoftClose}
        onRequestDuplicate={onRequestDuplicate}
        onRequestModifyAccess={onRequestModifyAccess}
        onFilterChange={onFilterChange}
        portal={rest.portal}
        page="productsList"
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        sortModel={sortModel}
        setSortModel={setSortModel}
        resultsPerPage={resultsPerPage}
        onRequestView={onRequestView}
      />
      {(() => {
        switch (rest.portal) {
          case 'banks':
            return (
              <div>
                <BanksProductCreateDialog
                  open={state === 'create'}
                  onSubmit={rest.onCreate}
                  onClose={rest.onClose}
                />
                <ProductUploadAllDialog
                  open={state === 'upload'}
                  onClose={rest.onClose}
                  onSubmit={rest.onUploadAllProducts ?? undefined}
                  onDownloadAllProducts={rest.onDownloadAllProducts}
                />
                <FSCSUploadDialogBank
                  open={state === 'upload-brochure'}
                  onClose={rest.onClose}
                />
              </div>
            );
          case 'ops':
            return (
              <OpsProductCreateDialog
                open={state === 'create'}
                onSubmit={rest.onCreate}
                onClose={rest.onClose}
              />
            );
          default:
            absurd(rest);
            break;
        }
      })()}
      {target ? (
        <>
          {rest.portal === 'ops' && (
            <>
              {state === 'update' && (
                <ProductUpdateDialog
                  open={state === 'update'}
                  product={target as BankProductResponse}
                  onSubmit={onUpdate}
                  onClose={rest.onClose}
                />
              )}
              {state === 'delete' && (
                <ProductCloseDialog
                  open={state === 'delete'}
                  onClose={rest.onClose}
                  product={target as BankPortalBankProductResponse}
                  onSubmit={onDelete ?? undefined}
                />
              )}
              {state === 'soft-close' && (
                <ProductSoftCloseDialog
                  open={state === 'soft-close'}
                  onClose={rest.onClose}
                  product={target as BankPortalBankProductResponse}
                  onSubmit={onSoftClose ?? undefined}
                />
              )}
            </>
          )}
          {state === 'update-rate' && (
            <ProductRateChangeDialog
              open={state === 'update-rate'}
              product={target}
              onSubmit={onUpdateRate}
              onClose={rest.onClose}
            />
          )}
          {state === 'modify-access' && (
            <PlatformProductDialog
              bankUid={target.bankUid}
              productUid={target.productUid}
              open={true}
              onClose={rest.onClose}
            />
          )}
        </>
      ) : null}
    </>
  );
};
